import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, SERVING_ROLES } from "@/lib/site";

const WORSHIP_BG =
  "https://images.unsplash.com/photo-1478147427282-58a87a120781?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const emptyForm = { full_name: "", email: "", phone: "", city: "", message: "" };

export const GetInvolved = () => {
  const [form, setForm] = useState(emptyForm);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggleRole = (role) =>
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.city) {
      toast.error("Please fill in your name, email, phone, and city.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/interest", { ...form, roles });
      setDone(true);
      toast.success("Thank you! We've received your information.");
      setForm(emptyForm);
      setRoles([]);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="involved" data-testid="involved-section" className="relative py-24 sm:py-32 px-6 sm:px-10 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url(${WORSHIP_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#06040A] via-[#06040A]/90 to-[#06040A]" />

      <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:sticky lg:top-28"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-5">Get Involved</p>
          <h2 className="font-display text-4xl sm:text-6xl font-semibold text-[#F9F6F0] leading-tight mb-6">
            Be Part Of The <span className="gold-text italic">Foundation</span>
          </h2>
          <p className="text-[#A19CA8] text-lg leading-relaxed mb-6">
            Whether you want to learn more about the ministry or you feel called to serve, fill out
            the form and our team will reach out. Select every area you'd love to serve in — you can
            choose as many as you like.
          </p>
          <ul className="space-y-3 text-[#A19CA8]">
            {["Learn more about the ministry", "Discover your place to serve", "Help launch a movement in Clayton County"].map(
              (t) => (
                <li key={t} className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-[#D4AF37] shrink-0" />
                  <span>{t}</span>
                </li>
              )
            )}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-[#130A1F]/70 backdrop-blur-xl border border-[#D4AF37]/25 p-7 sm:p-10"
        >
          {done ? (
            <div className="text-center py-16" data-testid="form-success">
              <PartyPopper className="h-14 w-14 text-[#D4AF37] mx-auto mb-6" />
              <h3 className="font-display text-3xl text-[#F9F6F0] mb-3">You're In!</h3>
              <p className="text-[#A19CA8] mb-8">
                Thank you for stepping forward. We'll be in touch soon as we prepare to launch.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="form-another-btn"
                className="border-2 border-[#D4AF37] text-[#D4AF37] uppercase tracking-wider text-sm px-7 py-3 hover:bg-[#D4AF37]/10 transition-all"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="interest-form">
              <Field label="Full Name" required>
                <input
                  data-testid="input-full-name"
                  value={form.full_name}
                  onChange={update("full_name")}
                  placeholder="Jane Doe"
                  className="ccc-input"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Email" required>
                  <input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className="ccc-input"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(404) 000-0000"
                    className="ccc-input"
                  />
                </Field>
              </div>
              <Field label="City" required>
                <input
                  data-testid="input-city"
                  value={form.city}
                  onChange={update("city")}
                  placeholder="Jonesboro, GA"
                  className="ccc-input"
                />
              </Field>

              <div>
                <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-3">
                  Where Would You Like To Serve?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-72 overflow-y-auto pr-1" data-testid="roles-group">
                  {SERVING_ROLES.map((role) => {
                    const active = roles.includes(role);
                    return (
                      <button
                        type="button"
                        key={role}
                        onClick={() => toggleRole(role)}
                        data-testid={`role-${role.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className={`flex items-center gap-2.5 px-3.5 py-2.5 text-left text-sm border transition-all duration-200 ${
                          active
                            ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F9F6F0]"
                            : "border-[#D4AF37]/20 text-[#A19CA8] hover:border-[#D4AF37]/50"
                        }`}
                      >
                        <span
                          className={`h-4 w-4 shrink-0 border flex items-center justify-center ${
                            active ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#D4AF37]/50"
                          }`}
                        >
                          {active && <Check className="h-3 w-3 text-[#06040A]" />}
                        </span>
                        {role}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label="Anything else? (optional)">
                <textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={3}
                  placeholder="Tell us a little about you..."
                  className="ccc-input resize-none"
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-interest-btn"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B5952F] text-[#06040A] font-semibold uppercase tracking-wider text-sm px-8 py-4 hover:from-[#F3D56D] hover:to-[#D4AF37] transition-all duration-300 shadow-[0_0_20px_rgba(212,175,55,0.3)] disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send My Information"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </label>
    {children}
  </div>
);
